/**
 * Workspace top-level tabs — the 6 destinations the router can navigate to.
 *
 * Shared cross-package: `apps/client` Zustand store, `@ando/core` query
 * analyzer, action_type→tab mapping, and any future LLM-backed route
 * decoder all consume this single enum.
 *
 * Wire shape: every BE event that hints at a routing destination MUST
 * use one of these literals (no free-form strings). The router rejects
 * unknown tabs at the parse boundary instead of silently falling back —
 * if a new vertical lands BE-side, this enum is the first thing to grow.
 */

import { z } from "zod";

/**
 * 10 daily-driver tabs.
 *
 * May 23, 2026 IA cleanup added `canvases` / `automations` / `shared`
 * (previously inside the avatar drawer's "Studio" block; UX audit
 * revealed weekly-to-daily usage with active chat context).
 *
 * May 26, 2026 — ``dashboards`` promoted from the floating right-edge
 * ``OutputRail`` (a separate chrome surface alongside ``canvas``) to a
 * first-class working-bar tab. The rail itself was deleted: Canvas was
 * already a tab (double-counted), Dashboard now is, and the rail's
 * "secondary nav" pattern was harder to discover than a top tab
 * (user-reported "è scomodo"). Removing the rail collapses two
 * orthogonal navigation surfaces into one. Order reflects expected
 * usage frequency.
 */
export const WORKSPACE_TABS = [
  "conversations",
  "calendar",
  "documents",
  "finance",
  // Jun 2026 — Sales (CRM: lead/clienti/opportunità/pipeline) split out of
  // the cross-cutting "work" tab so the CRM-orchestration surface has a home;
  // "work" reverts to pure tasks + Ando suggestions. (Marketing was scoped out
  // — too saturated/undifferentiated; Ando stays a system-of-action, not a suite.)
  "sales",
  "technical",
  "work",
  "canvases",
  "dashboards",
  "automations",
  // Jun 2026 — Hive Flows: visual node-based workflow builder for
  // non-technical users (the more powerful evolution of ``automations``).
  // First-class tab so the builder is discoverable, not URL-only.
  "flows",
  "shared",
  // Sep 2026 — chat shares hub ("Condivisioni": conversations and answers
  // sent to links / colleagues / email). ``shared`` above is Ando Connect.
  "shares",
] as const;

export const WorkspaceTabSchema = z.enum(WORKSPACE_TABS);

export type WorkspaceTab = z.infer<typeof WorkspaceTabSchema>;

/** Surface = sub-tab within a workspace tab (e.g. `"sdi"` inside `"finance"`). */
export type WorkspaceSurface = string;
